<template>
  <card-container
    :id="pageId"
    :is-loading="isLoading"
    :loading-message="`Sending Delivery History to ${formData.emailAddress}...`"
    :disabled="!isPhoneNumberValid || !isEmailValid || isLoading"
    @submit="submitForm"
    @reset="resetForm"
  >
    <template #title>
      Send Delivery History
    </template>

    <template #content>
      <fieldset>
        <div class="row">
          <label class="col-sm-3 control-label pt-10"> Email Address* </label>
          <div class="col-sm-8">
            <email-input
              v-model="formData.emailAddress"
              required
              @update-validation="updateEmailValidation"
            />
          </div>
        </div>

        <div class="row align-items-center">
          <label class="col-sm-3 control-label"> Phone Number* </label>
          <phone-number-input
            v-model="formData.phoneNumber"
            class="col-sm-8"
          />
        </div>
      </fieldset>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Duration* </label>
        <div class="col-sm-8">
          <date-picker
            v-model="timeSpanValue"
            type="daterange"
            required
          />
        </div>
      </div>
    </template>

    <template #submit-button>
      Send Delivery History
    </template>
  </card-container>
</template>

<script lang="ts">
import { ref, Ref, reactive, computed, watch, onMounted, onBeforeUnmount } from 'vue';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { useGlobal } from '@/use/global';
import { sendPost } from '@/Utils/api';

interface FormData {
  emailAddress: string;
  phoneNumber: string;
  fromDate: string;
  toDate: string;
}

const pageId = 'send-delivery-history';

export default {
  name: 'SendDeliveryHistory',

  setup: () => {
    const { customerInfo } = useCustomer();
    const { showSuccessPopupDialog, showErrorPopupDialog } = useSwalDialog();
    const { lastFormData, setFormData } = useGlobal(pageId);

    const formData: FormData = reactive({
      emailAddress: '',
      phoneNumber: '',
      fromDate: '',
      toDate: '',
    });
    const timeSpanValue: Ref<any> = ref(null);
    const isLoading = ref(false);
    const isEmailValid = ref(true);

    const isPhoneNumberValid = computed(() => util.isPhoneNumberValid(formData.phoneNumber));

    const updateEmailValidation = (value: boolean) => {
      isEmailValid.value = value;
    };

    const submitForm = async () => {
      isLoading.value = true;
      try {
        const response = await sendPost('sendDeliveryHistory', formData);
        showSuccessPopupDialog({
          text: response.data.message,
        });

        resetForm();
      } catch (error) {
        const errorText = util.getErrorResponse(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      }
      isLoading.value = false;
    };

    const resetForm = () => {
      formData.emailAddress = '';
      formData.phoneNumber = '';
      formData.fromDate = '';
      formData.toDate = '';

      timeSpanValue.value = null;
    };

    watch(
      customerInfo,
      (value: any) => {
        resetForm();
        if (value?.id) {
          formData.phoneNumber = value.phoneNumber;
          formData.emailAddress = value.email;
        }
      },
      {
        immediate: true,
      },
    );

    watch(timeSpanValue, (value: any) => {
      if (value) {
        formData.fromDate = util.formatDateTime(value[0], 'YYYY-MM-DD');
        formData.toDate = util.formatDateTime(value[1], 'YYYY-MM-DD');
      } else {
        formData.fromDate = '';
        formData.toDate = '';
      }
    });

    onMounted(() => {
      formData.phoneNumber = lastFormData.value?.phoneNumber;
      formData.emailAddress = lastFormData.value?.emailAddress;
      formData.fromDate = lastFormData.value?.fromDate || '';
      formData.toDate = lastFormData.value?.toDate || '';
      if (formData.fromDate && formData.toDate) {
        timeSpanValue.value = [formData.fromDate, formData.toDate];
      }
    });

    onBeforeUnmount(() => {
      setFormData(formData);
    });

    return {
      pageId,
      formData,
      timeSpanValue,
      isLoading,
      isPhoneNumberValid,
      isEmailValid,

      submitForm,
      resetForm,
      updateEmailValidation,
    };
  },
};
</script>
